import {action, makeObservable, observable, runInAction} from 'mobx';
import nextFrame from 'next-frame';

import {delay} from '@yourcoach/async-utils';

import type {OmitRecursively} from '../../utils/utility-types';
import type {ApiMethodName, ApiResourceName, Entity} from '../types';
import type {ApiRequestOptions, ApiRpcRequestParams} from './apiRequest';

import {isCoreOrB2b} from '../../utils/coreOrB2b/isCoreOrB2b';
import {pickCoreOrB2b} from '../../utils/coreOrB2b/pickCoreOrB2b';
import {isTestEnv} from '../../utils/test-utils/isTestEnv';
import {isDefined} from '../../utils/typecheck';
import {apiRequest} from './apiRequest';
import {expandObj} from './expand';

export interface BatchMethodStatus<T> {
  entities: T[];
  error: any;
  inProgress: boolean;
  success: boolean;
}

export interface MethodStatus<T> {
  entity: null | T;
  error: any;
  inProgress: boolean;
  success: boolean;
}

interface CrudMethods {
  create?: ApiMethodName;
  delete?: ApiMethodName;
  fetch?: ApiMethodName;
  update?: ApiMethodName;
}

interface Options<T> {
  [key: string]: any;

  applyParams?: (entity: T) => ApiRpcRequestParams;
}

export class EntityStore<
  T extends Entity,
  FP extends object = ApiRpcRequestParams,
  CP extends object = ApiRpcRequestParams,
  UP extends object = ApiRpcRequestParams,
  DP extends object = ApiRpcRequestParams,
> {
  _methods: CrudMethods;
  _options: Options<T>;
  _type: ApiResourceName;
  creating: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };
  creatingBatch: BatchMethodStatus<T> = {
    entities: [],
    error: null,
    inProgress: false,
    success: false,
  };
  deleting: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };
  deletingBatch: BatchMethodStatus<T> = {
    entities: [],
    error: null,
    inProgress: false,
    success: false,
  };
  fetching: Omit<MethodStatus<T>, 'entity'> = {
    error: null,
    inProgress: false,
    success: false,
  };
  fetchingBatch: Omit<BatchMethodStatus<T>, 'entities'> = {
    error: null,
    inProgress: false,
    success: false,
  };
  updating: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };
  updatingBatch: BatchMethodStatus<T> = {
    entities: [],
    error: null,
    inProgress: false,
    success: false,
  };

  constructor(
    type: ApiResourceName = 'entity' as ApiResourceName,
    crudMethods: CrudMethods = {
      create: undefined,
      delete: undefined,
      fetch: undefined,
      update: undefined,
    },
    options: Options<T> = {},
  ) {
    this._type = type;
    this._methods = crudMethods;
    this._options = options;

    makeObservable(this, {
      create: !isTestEnv ? action.bound : false,
      createBatch: !isTestEnv ? action.bound : false,
      creating: observable.shallow,
      creatingBatch: observable.shallow,
      delete: !isTestEnv ? action.bound : false,
      deleteBatch: !isTestEnv ? action.bound : false,
      deleting: observable.shallow,
      deletingBatch: observable.shallow,
      fetch: !isTestEnv ? action.bound : false,
      fetchBatch: !isTestEnv ? action.bound : false,
      fetching: observable.shallow,
      fetchingBatch: observable.shallow,
      localUpdate: !isTestEnv ? action.bound : false,
      localUpdateBatch: !isTestEnv ? action.bound : false,
      update: !isTestEnv ? action.bound : false,
      updateBatch: !isTestEnv ? action.bound : false,
      updating: observable.shallow,
      updatingBatch: observable.shallow,
    });
  }

  _checkMethod(method: string, isBatch = false) {
    if (!this._methods[method]) {
      throw new Error(
        `EntityStore(type: ${this._type}): method ${method}${
          isBatch ? 'Batch' : ''
        } is not available for use!`,
      );
    }
  }

  _createBatchMethod<P extends object = ApiRpcRequestParams>({
    applyParams,
    method,
    onBefore,
    onFail,
    onSuccess,
    options,
  }: {
    applyParams?: (entity: T) => ApiRpcRequestParams;
    method: ApiMethodName;
    onBefore?: (entities: T[]) => void;
    onFail?: (entities: T[], error: any) => void;
    onSuccess?: (entities: T[]) => void;
    options?: ApiRequestOptions[];
  }) {
    return async (items: {entity: T; params?: Omit<P, '_id'>}[]) => {
      try {
        if (onBefore) {
          await onBefore(items.map(item => item.entity));
        }

        const results = (await apiRequest({
          batch: items.map((item, i) => ({
            method,
            options: options && options[i],
            params: {
              ...(this._options.applyParams
                ? this._options.applyParams(item.entity)
                : null),
              ...(applyParams ? applyParams(item.entity) : null),
              ...item.params,
              _id: item.entity._id,
            },
          })),
        })) as any[];

        const entities = await this._processBatchResult(results, items);

        if (onSuccess) {
          await onSuccess(entities);
        }

        return entities;
      } catch (error) {
        if (onFail) {
          await onFail(
            items.map(item => item.entity),
            error,
          );
        }

        throw error;
      }
    };
  }

  _createMethod<P extends object = ApiRpcRequestParams>({
    applyParams,
    method,
    onBefore,
    onFail,
    onSuccess,
    options,
  }: {
    applyParams?: (entity: T) => P;
    method: ApiMethodName;
    onBefore?: (entity: T) => void;
    onFail?: (entity: T, error: any) => void;
    onSuccess?: (entity: T) => void;
    options?: ApiRequestOptions;
  }) {
    return async (entity: T, params?: Omit<P, '_id'>) => {
      try {
        if (onBefore) {
          await onBefore(entity);
        }

        const result = await apiRequest({
          method,
          options,
          params: {
            ...(this._options.applyParams
              ? this._options.applyParams(entity)
              : null),
            ...(applyParams ? applyParams(entity) : null),
            ...params,
            _id: entity._id,
          },
        });

        const _entity = this._processResult(result, params);

        if (onSuccess) {
          await onSuccess(_entity);
        }

        return _entity;
      } catch (error) {
        if (onFail) {
          await onFail(entity, error);
        }

        throw error;
      }
    };
  }

  async _processBatchResult(
    results: any,
    items: {entity?: T; params?: ApiRpcRequestParams}[],
  ) {
    const entities: T[] = [];
    const resultsLength = results.length;

    for (let i = 0; i < resultsLength; i += 1) {
      let entity: T;

      const result = results[i];
      const item = items[i]!;

      if (item.params && item.params.expand) {
        entity = expandObj<T>(result[this._type], {
          expand: item.params.expand,
          expanded: result._expanded,
        });
      } else {
        entity = result[this._type];
      }

      entities[i] = entity;

      await nextFrame();
    }

    return entities;
  }

  _processResult(result: any, params?: ApiRpcRequestParams) {
    let entity: T;

    if (params && params.expand) {
      entity = expandObj<T>(result[this._type], {
        expand: params.expand,
        expanded: result._expanded,
      });
    } else {
      entity = result[this._type];
    }

    return entity;
  }

  async create(params: CP) {
    try {
      this._checkMethod('create');

      runInAction(() => {
        this.creating = {
          entity: null,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      if (isCoreOrB2b(params)) {
        params = pickCoreOrB2b(params) as CP;
      }

      const result = await apiRequest({
        method: this._methods.create!,
        params,
      });

      const entity = this._processResult(result, params);

      runInAction(() => {
        this.creating = {
          ...this.creating,
          entity,
          inProgress: false,
          success: true,
        };
      });

      return entity;
    } catch (error) {
      runInAction(() => {
        this.creating = {
          ...this.creating,
          error,
          inProgress: false,
        };
      });

      throw error;
    }
  }

  async createBatch(params: CP[]) {
    try {
      this._checkMethod('create', true);

      runInAction(() => {
        this.creatingBatch = {
          entities: [],
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const results = (await apiRequest({
        batch: params.map(item => {
          if (isCoreOrB2b(item)) {
            item = pickCoreOrB2b(item) as CP;
          }

          return {
            method: this._methods.create!,
            params: item,
          };
        }),
      })) as any[];

      const entities = await this._processBatchResult(
        results,
        params.map(item => ({params: item})),
      );

      runInAction(() => {
        this.creatingBatch = {
          ...this.creatingBatch,
          entities,
          inProgress: false,
          success: true,
        };
      });

      return entities;
    } catch (error) {
      runInAction(() => {
        this.creatingBatch = {
          ...this.creatingBatch,
          error,
          inProgress: false,
        };
      });

      throw error;
    }
  }

  async delete(entity: T, params?: OmitRecursively<DP, '_id'>) {
    try {
      runInAction(() => {
        this.deleting = {
          entity,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      this._checkMethod('delete');

      if (isDefined(params) && isCoreOrB2b(params)) {
        params = pickCoreOrB2b(params) || ({} as OmitRecursively<DP, '_id'>);
      }

      await apiRequest({
        method: this._methods.delete!,
        params: {
          ...(this._options.applyParams
            ? this._options.applyParams(entity)
            : null),
          ...params,
          _id: entity._id,
        },
      });

      runInAction(() => {
        this.deleting = {
          ...this.deleting,
          inProgress: false,
          success: true,
        };
      });

      return entity;
    } catch (error) {
      runInAction(() => {
        this.deleting = {
          ...this.deleting,
          error,
          inProgress: false,
        };
      });

      throw error;
    }
  }

  async deleteBatch(items: {entity: T; params?: OmitRecursively<DP, '_id'>}[]) {
    try {
      this._checkMethod('delete', true);

      const entities = items.map(x => x.entity);

      runInAction(() => {
        this.deletingBatch = {
          entities,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      (await apiRequest({
        batch: items.map((item, i) => {
          if (isDefined(item.params) && isCoreOrB2b(item.params)) {
            item.params =
              pickCoreOrB2b(item.params) || ({} as OmitRecursively<DP, '_id'>);
          }

          return {
            method: this._methods.delete!,
            params: {
              ...(this._options.applyParams
                ? this._options.applyParams(item.entity)
                : null),
              ...(items[i]!.params ? items[i]!.params : null),
              _id: item.entity._id,
            },
          };
        }),
      })) as any[];

      runInAction(() => {
        this.deletingBatch = {
          ...this.deletingBatch,
          inProgress: false,
          success: true,
        };
      });

      return entities;
    } catch (error) {
      runInAction(() => {
        this.deletingBatch = {
          ...this.deletingBatch,
          error,
          inProgress: false,
        };
      });

      throw error;
    }
  }

  async fetch(params: FP, options?: ApiRequestOptions) {
    try {
      this._checkMethod('fetch');

      runInAction(() => {
        this.fetching = {
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const response = await apiRequest<FP>({
        method: this._methods.fetch!,
        options,
        params,
      });

      const result = this._processResult(response, params);

      runInAction(() => {
        this.fetching = {
          error: null,
          inProgress: false,
          success: true,
        };
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.fetching = {
          error,
          inProgress: false,
          success: false,
        };
      });

      throw error;
    }
  }

  async fetchBatch(params: FP[], options?: ApiRequestOptions[]) {
    try {
      this._checkMethod('fetch', true);

      runInAction(() => {
        this.fetchingBatch = {
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const responses = (await apiRequest<FP>({
        batch: params.map((item, i) => ({
          method: this._methods.fetch!,
          options: options && options[i],
          params: item,
        })),
      })) as any[];

      const result = await this._processBatchResult(
        responses,
        params.map(item => ({params: item})),
      );

      runInAction(() => {
        this.fetchingBatch = {
          error: null,
          inProgress: false,
          success: true,
        };
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.fetchingBatch = {
          error,
          inProgress: false,
          success: false,
        };
      });

      throw error;
    }
  }

  async localUpdate(entity: T, params: ApiRpcRequestParams) {
    runInAction(() => {
      this.updating = {
        entity,
        error: null,
        inProgress: true,
        success: false,
      };
    });

    await delay(10);

    const updatedEntity = {
      ...entity,
      ...(this._options.applyParams ? this._options.applyParams(entity) : null),
      ...params,
    };

    runInAction(() => {
      this.updating = {
        ...this.updating,
        entity: updatedEntity,
        inProgress: false,
        success: true,
      };
    });

    return updatedEntity;
  }

  async localUpdateBatch(items: {entity: T; params: ApiRpcRequestParams}[]) {
    runInAction(() => {
      this.updatingBatch = {
        entities: items.map(x => x.entity),
        error: null,
        inProgress: true,
        success: false,
      };
    });

    await delay(10);

    const updatedEntities: T[] = [];

    items.forEach((item, i) => {
      updatedEntities[i] = {
        ...item.entity,
        ...(this._options.applyParams
          ? this._options.applyParams(item.entity)
          : null),
        ...item.params,
      };
    });

    runInAction(() => {
      this.updatingBatch = {
        ...this.updatingBatch,
        entities: updatedEntities,
        inProgress: false,
        success: true,
      };
    });

    return updatedEntities;
  }

  async update(entity: T, params: OmitRecursively<UP, '_id'>) {
    try {
      this._checkMethod('update');

      runInAction(() => {
        this.updating = {
          entity,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      if (isCoreOrB2b(params)) {
        params = pickCoreOrB2b(params) || ({} as OmitRecursively<UP, '_id'>);
      }

      const result = await apiRequest({
        method: this._methods.update!,
        params: {
          ...(this._options.applyParams
            ? this._options.applyParams(entity)
            : null),
          ...params,
          _id: entity._id,
        },
      });

      const updatedEntity = this._processResult(result, params);

      runInAction(() => {
        this.updating = {
          ...this.updating,
          entity: updatedEntity,
          inProgress: false,
          success: true,
        };
      });

      return updatedEntity;
    } catch (error) {
      runInAction(() => {
        this.updating = {
          ...this.updating,
          error,
          inProgress: false,
        };
      });

      throw error;
    }
  }

  async updateBatch(items: {entity: T; params: OmitRecursively<UP, '_id'>}[]) {
    try {
      this._checkMethod('update', true);

      runInAction(() => {
        this.updatingBatch = {
          entities: items.map(x => x.entity),
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const results = (await apiRequest({
        batch: items.map(item => {
          if (isCoreOrB2b(item.params)) {
            item.params =
              pickCoreOrB2b(item.params) || ({} as OmitRecursively<UP, '_id'>);
          }

          return {
            method: this._methods.update!,
            params: {
              ...(this._options.applyParams
                ? this._options.applyParams(item.entity)
                : null),
              ...item.params,
              _id: item.entity._id,
            },
          };
        }),
      })) as any[];

      const updatedEntities = await this._processBatchResult(results, items);

      runInAction(() => {
        this.updatingBatch = {
          ...this.updatingBatch,
          entities: updatedEntities,
          inProgress: false,
          success: true,
        };
      });

      return updatedEntities;
    } catch (error) {
      runInAction(() => {
        this.updatingBatch = {
          ...this.updatingBatch,
          error,
          inProgress: false,
        };
      });

      throw error;
    }
  }
}
